// src/services/pdf/sections/PatientNotesSection.ts
// Renders Section 4: "In her own words" — verbatim, unedited patient notes.

import { PDFDocument } from "../core/PDFDocument";
import { PDF_COLORS } from "../core/colors";
import { PAGE_LAYOUT } from "../core/layout";
import type { PDFRenderData } from "../types";
import type { SectionRenderer } from "./SectionRenderer";

export class PatientNotesSection implements SectionRenderer {
  readonly id = "patientNotes";

  render(doc: PDFDocument, data: PDFRenderData): void {
    const { notes } = data;
    if (!notes || notes.trim().length === 0) return;

    doc.ensureSpace(20);
    doc.drawHorizontalRule();

    // Section title
    doc.setFont("bold", PAGE_LAYOUT.fontSize.sectionTitle);
    doc.setColor(PDF_COLORS.brandPrimary);
    doc.text("IN HER OWN WORDS", doc.contentLeft);
    doc.advanceY(2);

    doc.setFont("italic", PAGE_LAYOUT.fontSize.tiny);
    doc.setColor(PDF_COLORS.textMuted);
    doc.text("Reproduced verbatim - not paraphrased or edited by AI.", doc.contentLeft);
    doc.advanceY(5);

    // Quote background box
    const textHeight = doc.getTextHeight(notes, doc.contentWidth - 12);
    const boxHeight = textHeight + 8;

    doc.ensureSpace(boxHeight + 4);
    const boxY = doc.y;

    doc.drawRect(
      doc.contentLeft, boxY,
      doc.contentWidth, boxHeight,
      { fill: PDF_COLORS.bgLight, radius: 2 }
    );

    // Left accent bar
    doc.drawRect(
      doc.contentLeft, boxY,
      2, boxHeight,
      { fill: PDF_COLORS.brandLight }
    );

    // Quote text
    doc.advanceY(4);
    doc.setFont("normal", PAGE_LAYOUT.fontSize.body);
    doc.setColor(PDF_COLORS.textPrimary);
    doc.writeWrapped(`"${notes.trim()}"`, {
      x: doc.contentLeft + 6,
      maxWidth: doc.contentWidth - 12,
    });

    doc.advanceY(PAGE_LAYOUT.sectionGap);
  }
}
